<script setup>
import gsap from '~/vendors/gsap'
import mobile from 'is-mobile'

const props = defineProps({
  filters: {
    type: Array,
    required: true,
  },
  filtersButtonLabel: {
    type: String,
    default: 'Sort by',
  },
  filtersButtonLabelMobile: {
    type: String,
    default: 'Sort by',
  },
  filtersButtonIconMobile: {
    type: String,
    default: 'sort',
  },
  filtersButtonRule: {
    type: Boolean,
    default: true,
  },
  modalId: {
    type: String,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
})

const componentRef = ref(null)
const modal = ref(null)
const open = computed(() => props.open)
const isMobile = ref(false)
const activeFilters = ref([])

const emit = defineEmits(['filter-opened', 'filter-closed', 'filter-changed'])

watch(activeFilters.value, (value) => {
  emit('filter-changed', value)
})

let openTimeline
let closeTimeline
function initTimelines() {
  openTimeline = gsap
    .timeline({ paused: true })
    .call(() => {
      modal.value.style.display = 'block'
    })
    .to(modal.value, {
      duration: 0.7,
      height: 'auto',
      alpha: 1,
      ease: 'power2.out',
    })

  closeTimeline = gsap
    .timeline({ paused: true })
    .to(modal.value, {
      duration: 0.3,
      height: 0,
      alpha: 0,
      ease: 'power2.out',
    })
    .call(() => {
      modal.value.style.display = 'none'
    })
}

watch(open, (value) => {
  if (value) {
    openTimeline?.play(0)
  } else {
    closeTimeline?.play(0)
  }
})


function onToggle() {
  if (props.open) {
    emit('filter-closed', props.modalId)
  } else {
    emit('filter-opened', props.modalId)
  }
}

function onCheckboxClick(e) {
  if (e.currentTarget.checked) {
    activeFilters.value.push(e.currentTarget.name)
  } else {
    const index = activeFilters.value.indexOf(e.currentTarget.name)
    if (index > -1) {
      activeFilters.value.splice(index, 1) // 2nd parameter means remove one item only
    }
  }
}

function onSelectChange(e) {
  const selected = [...e.currentTarget.options]
    .filter((option) => option.selected)
    .map((option) => option.value)

  activeFilters.value = selected
}

const outSideClick = (event) => {
  if (props.open && !(componentRef.value == event.target || componentRef.value.contains(event.target))) {
    emit('filter-closed', props.modalId)
  }
}

// Lifecycle
onMounted(() => {
  modal.value.style.display = 'none'
  modal.value.style.height = 0
  initTimelines()

  isMobile.value = mobile()

  document.addEventListener('click', outSideClick);
})

onBeforeUnmount(() => {
  openTimeline?.kill()
  closeTimeline?.kill()

  document.removeEventListener('click', outSideClick);
})
</script>

<template>
  <div
    ref="componentRef"
    :aria-controls="modalId"
    :aria-expanded="open"
    class="dropdown-filters"
    :class="`${open ? 'dropdown-filters--open' : ''} ${
      isMobile ? 'dropdown-filters--mobile' : ''
    }`"
  >
    <div class="dropdown-filters__header">
      <button
        type="button"
        class="dropdown-filters__button text__eyebrow-medium"
        @click="onToggle"
      >
        <span>{{ filtersButtonLabel }}</span>
        <Icon name="chevron-down" />
      </button>

      <HorizontalRule
        v-if="filtersButtonRule"
        class="dropdown-filters__divider"
      />

      <div class="dropdown-filters__select-wrapper dropdown-filters__button">
        <Icon :name="filtersButtonIconMobile" />
        <span class="text__eyebrow-medium"
          >{{ filtersButtonLabelMobile }}
        </span>
        <select
          class="dropdown-filters__select"
          :name="`${modalId}-select`"
          multiple
          @change="onSelectChange"
        >
          <option
            v-for="filter in filters"
            :key="`${filter.id}-mobile`"
            :value="filter.value"
            :selected="activeFilters.includes(filter.value)"
          >
            {{ activeFilters.includes(filter.value) }}
            {{ filter.label }}
          </option>
        </select>
      </div>
    </div>

    <div ref="modal" class="dropdown-filters__modal" :id="modalId">
      <ul class="dropdown-filters__list">
        <li
          class="dropdown-filters__list-item dropdown-filters__list-item--close"
        >
          <button
            type="button"
            class="text__eyebrow-medium dropdown-filters__list-close"
            @click="emit('filter-closed', modalId)"
          >
            Close
            <Icon name="plus" />
          </button>
        </li>
        <li
          v-for="filter in filters"
          :key="`${filter.id}-desktop`"
          class="dropdown-filters__list-item"
        >
          <input
            @click="onCheckboxClick"
            type="checkbox"
            :id="filter.id"
            :name="filter.value"
            :checked="activeFilters.includes(filter.value)"
          />

          <label class="text__eyebrow-medium" :for="filter.id">{{
            filter.label
          }}</label>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss">
// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';
@import 'styles/utils/timing-functions';

// Vendors
@import 'styles/vendors/sass-mq/mq';

.dropdown-filters {
  position: relative;
  pointer-events: none;

  &__select {
    // display: none;
    pointer-events: auto;

    @include mq($until: 'l') using ($until) {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    &-wrapper {
      @include mq($until: 'l') using ($until) {
        position: relative;

        .svg {
          margin-top: -3px;
          margin-right: 8px;
        }
      }
    }
  }

  &__header {
    pointer-events: none;
    flex-shrink: 0;
  }

  &__button {
    padding: 10px 0px;
    display: flex;
    align-items: center;
    color: $brand-white;
    pointer-events: auto;

    > span {
      flex-shrink: 0;
    }

    .svg {
      position: relative;
      top: -1px;
      width: 16px;
      margin-left: 8px;

      @include mq($until: 'l') using ($until) {
        margin-bottom: -4px;
      }
    }

    @include mq($until: 'l') using ($until) {
      margin: 0 auto;
      height: 52px;
      padding-left: 38px;
      padding-right: 38px;
      line-height: 1;
      display: flex;
      color: $brand-white;
      align-items: center;
      justify-content: center;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
        linear-gradient(
          90.21deg,
          #c80afb -22.51%,
          rgba(200, 10, 251, 0.47) 10.24%,
          rgba(200, 10, 251, 0) 115.51%
        ),
        #200929;
      box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12);
      border-radius: 42px;
    }
  }

  &__divider {
    margin-top: -23px;

    @include mq($until: 'l') using ($until) {
      display: none;
    }
  }

  &__modal {
    padding: 0px 38px;
    position: absolute;
    top: 70px;
    left: 0;
    width: gs(7, 7, 'l');
    border: 1px solid #1d083a;
    border-radius: 20px;
    background-color: #130725;
    z-index: 10;
    overflow: hidden;
    height: 0;
    overflow: auto;
    pointer-events: auto;
    opacity: 0;
    max-height: calc(100% - 80px);

    @include mq($until: 'l') using ($until) {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      width: 100%;
      // position: relative;
      // top: auto;
      // width: 100%;
    }

    input {
      margin: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      appearance: none;
      cursor: pointer;

      &:hover + label,
      &:checked + label {
        color: $brand-white;

        &::before {
          border-color: $brand-magenta;
        }
      }

      &:checked + label {
        &::after {
          opacity: 1;
        }
      }
    }
  }

  &__list {
    &-item {
      position: relative;
      width: 100%;
      display: flex;

      &:first-child {
        margin-top: 16px;
      }

      &--close + .dropdown-filters__list-item {
        @include mq($from: 'l') using ($from) {
          margin-top: 16px;
        }
      }

      &:last-child {
        margin-bottom: 16px;
      }

      &:not(:last-child) {
        border-bottom: 1px solid rgba($brand-white, 0.2);
      }
    }

    .text__eyebrow-medium {
      display: block;
      padding-top: 22px;
      padding-bottom: 22px;

      &.dropdown-filters__list-close {
        display: inline-flex;
        align-items: center;
        color: $brand-white;
        margin-left: auto;

        .svg {
          position: relative;
          top: -1px;
          margin-left: 6px;
          width: 16px;
          transform: rotate(45deg);
        }
      }
    }

    label {
      color: rgba($brand-white, 0.5);
      cursor: pointer;
      transition: color 0.3s $ease-out-quad;

      &::before {
        position: absolute;
        top: 50%;
        right: 0px;
        width: 16px;
        height: 16px;
        border-radius: 4px;
        border: 2px solid rgba($brand-white, 0.2);
        content: '';
        display: block;
        transform: translateY(-50%);
        transition: border-color 0.3s $ease-out-quad;
      }

      &::after {
        position: absolute;
        top: 50%;
        right: 6px;
        width: 4px;
        height: 4px;
        border-radius: 4px;
        background-color: $brand-white;
        opacity: 0;
        content: '';
        transform: translateY(-50%);
        transition: opacity 0.3s $ease-out-quad;
      }
    }
  }

  // Small screen mobile
  &--mobile {
    .dropdown-filters__button:not(.dropdown-filters__select-wrapper) {
      @include mq($until: 'l') using ($until) {
        display: none;
      }
    }
  }

  // Small screen but not mobile
  &:not(.dropdown-filters--mobile) {
    @include mq($until: 'l') using ($until) {
      width: 100%;
      bottom: 0;
    }

    .dropdown-filters__button {
      @include mq($until: 'l') using ($until) {
        display: flex;
      }
    }

    .dropdown-filters__modal {
      @include mq($until: 'l') using ($until) {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        max-height: 50vh;
      }
    }

    .dropdown-filters__list-item--close {
      @include mq($from: 'l') using ($from) {
        display: none;
      }
    }

    .dropdown-filters__select-wrapper {
      display: none;
    }
  }
}
</style>
